import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState } from "react";
import * as d3 from "d3";
import "./chart.css";
import { MdLabel, MdLabelOff } from "react-icons/md";

const Charts = forwardRef(
  (
    {
      xData,
      yData,
      width = Math.max(xData.length * 30, 500),
      height = Math.max(yData.length, 500),
      drag = false,
      edit = false,
      forecastId
    },
    ref
  ) => {
    const svgRef = useRef();
    const tooltipRef = useRef();
    const zoomTransformRef = useRef(d3.zoomIdentity);
    const xScaleRef = useRef();
    const yScaleRef = useRef();
    const [action, setAction] = useState("switch");
    const [contextMenu, setContextMenu] = useState(null);
    const [leftClickMenu, setLeftClickMenu] = useState(null);
    const [startTime, setStartTime] = useState("");
    const [endTime, setEndTime] = useState("");
    const [selectedParameter, setSelectedParameter] = useState(null);
    const [chartData, setChartData] = useState(yData);
    const [chartWidth, setChartWidth] = useState(window.innerWidth * 0.96);
    const menuRef = useRef(null);
    const [showLegend, setShowLegend] = useState(false);
    const [legendSearch, setLegendSearch] = useState("");
    const baseYDataRef = useRef([]);
    const dragContextRef = useRef({});
    const [hiddenSeriesMap, setHiddenSeriesMap] = useState({});
    const initialChartHeight = 600;
    const [chartHeight, setChartHeight] = useState(window.innerHeight * 0.70);
    const [pivotState, setPivotState] = useState({});
    const [switchState, setSwitchState] = useState({});
    const [lineState, setLineState] = useState({});
    const [forceYMinZero, setForceYMinZero] = useState(false);
    const [yMin, setYMin] = useState(0);
    const [yMax, setYMax] = useState(0);
    useImperativeHandle(ref, () => ({
      getUpdatedChartData: () => {
        if (!baseYDataRef.current) return [];
        return baseYDataRef.current.map(series => {
          return {
            ...series,
            values: series.values.map((base, i) => {
              const key = `${series.field}_${i}`;
              const pivot = pivotState[key] || 0;
              const sw = switchState[key] || 0;
              const l = lineState[key] || 0;
              return base + pivot + sw + l;
            }),
          };
        });
      },
    }));

    useEffect(() => {
      setChartHeight(initialChartHeight);
    }, [yData]);

    // useEffect(() => {
    //   const initialMax = Math.ceil(d3.max(yData.flatMap(d => d.values)) * 1.05);
    //   setYMax(prev => Math.max(prev, initialMax));
    // }, [yData]);

    useEffect(() => {
      if (forecastId) {
        const stored = localStorage.getItem(`hiddenSeries_${forecastId}`);
        if (stored) {
          try {
            setHiddenSeriesMap(JSON.parse(stored));
          } catch (e) {
            // console.error("Invalid JSON in localStorage:", stored);
          }
        } else {
          setHiddenSeriesMap({});
        }
      }
    }, [forecastId]);

    useEffect(() => {
      if (forecastId) {
        localStorage.setItem(`hiddenSeries_${forecastId}`, JSON.stringify(hiddenSeriesMap));
      }
    }, [hiddenSeriesMap, forecastId]);

    useEffect(() => {
      if (!xData || !yData) return;
      setPivotState({});
      setSwitchState({});
      setLineState({});
      baseYDataRef.current = [];
    }, [xData, yData]);

    useEffect(() => {
      const updateChartSize = () => setChartWidth(window.innerWidth * 0.90);
      window.addEventListener("resize", updateChartSize);
      return () => window.removeEventListener("resize", updateChartSize);
    }, []);

    useEffect(() => {
      const updateChartSize = () => {
        setChartWidth(window.innerWidth * 0.90);
        setChartHeight(window.innerHeight * 0.70);
      };
      window.addEventListener("resize", updateChartSize);
      return () => window.removeEventListener("resize", updateChartSize);
    }, []);

    useEffect(() => {
      if (!yData || yData.length === 0) return;

      const allValues = yData.flatMap(d =>
        d.values.filter(v => typeof v === "number" && !isNaN(v))
      );

      if (allValues.length === 0) return;

      const min = Math.min(...allValues);
      const max = Math.max(...allValues);
      const paddedMax = Math.ceil(max * 1.05);

      setYMin(min);
      setYMax(paddedMax);
    }, [yData]);

    useEffect(() => {
      if (!xData || !yData) return;
      // console.log(xData);
      // console.log(yData);
const margin = { top: 20, right: 30, bottom: 60, left: 60 };
      const width = chartWidth;
      const height = chartHeight;

      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();
      svg.attr("width", width + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom)
        .style("background-color", "#F2F2F2");

      const maxLabels = Math.floor(width / 80);
      const interval = Math.ceil(xData.length / maxLabels);

      const chartGroup = svg.append("g")
        .attr("class", "chart-content")
        .attr("clip-path", "url(#clip)");

      const xScale = d3.scaleLinear()
        .domain([0, xData.length - 1])
        .range([margin.left, width - margin.right]);

      const yBuffer = 1000;
      const dynamicYMin = forceYMinZero ? 0 : Math.max(0, yMin - yBuffer);
      const dynamicYMax = yMax;
      const yScale = d3.scaleLinear()
        .domain([dynamicYMin, dynamicYMax])
        .range([height - margin.bottom, margin.top]);
      xScaleRef.current = xScale;
      yScaleRef.current = yScale;

      const xAxisGroup = svg.append("g")
        .attr("class", "x-axis")
        .attr("transform", `translate(0, ${height - margin.bottom})`);

      const yAxisGroup = svg.append("g")
        .attr("class", "y-axis")
        .attr("transform", `translate(${margin.left}, 0)`);

      const xAxis = d3.axisBottom(xScale)
        .tickFormat(i => xData[Math.round(i)])
        .ticks(xData.length / interval);

      const yAxis = d3.axisLeft(yScale);

      xAxisGroup.call(xAxis)
        .selectAll("text")
        .attr("transform", "rotate(-45)")
        .style("text-anchor", "end")
        .style("font-size", "14px")
        .style("font-weight", "bold");

      yAxisGroup.call(yAxis);
      yAxisGroup.selectAll("text")
        .style("font-size", "14px")
        .style("font-weight", "bold");

      const tooltip = d3.select(tooltipRef.current)
        .style("opacity", 0)
        .style("position", "absolute")
        .style("pointer-events", "none")
        .style("background-color", "rgba(0,0,0,0.6)")
        .style("color", "white")
        .style("padding", "5px")
        .style("border-radius", "4px");

      const grid = svg.append("g").attr("class", "grid");

      grid.selectAll("line.horizontal")
        .data(yScale.ticks())
        .enter()
        .append("line")
        .attr("x1", margin.left)
        .attr("x2", width - margin.right)
        .attr("y1", d => yScale(d))
        .attr("y2", d => yScale(d))
        .style("stroke", "#ccc")
        .style("stroke-width", 1)
        .style("shape-rendering", "crispEdges");

      grid.selectAll(".grid-line-vertical")
        .data(xScale.ticks(xData.length / interval))
        .enter()
        .append("line")
        .attr("class", "grid-line-vertical")
        .attr("x1", d => xScale(d))
        .attr("x2", d => xScale(d))
        .attr("y1", margin.top)
        .attr("y2", height - margin.bottom)
        .style("stroke", "#ccc")
        .style("stroke-width", 1)
        .style("stroke-dasharray", "4,4");

      svg.on("contextmenu", (event) => {
        if (!drag) return;
        event.preventDefault();
        const [mouseX, mouseY] = d3.pointer(event);
        const menuWidth = 160;
        const menuHeight = 100;
        const padding = 10;

        const clampedX = Math.min(mouseX, chartWidth - menuWidth - padding);
        const clampedY = Math.min(mouseY, chartHeight - menuHeight - padding);

        setContextMenu({ x: clampedX, y: clampedY });
        setLeftClickMenu(null);
      });

      d3.select(window).on("click", () => setContextMenu(null));

      if (baseYDataRef.current.length === 0) {
        baseYDataRef.current = JSON.parse(JSON.stringify(yData));
      }

      yData.forEach(series => {
        if (hiddenSeriesMap[series.field]) return;
        const line = d3.line()
          .defined(d => d !== 0)
          .x((_, i) => xScaleRef.current(i))
          .y((_, i) => {
            const key = `${series.field}_${i}`;
            const base = baseYDataRef.current.find(s => s.field === series.field)?.values[i] || 0;
            const pivot = pivotState[key] || 0;
            const sw = switchState[key] || 0;
            const l = lineState[key] || 0;
            return yScale(base + pivot + sw + l);
          });

        chartGroup.append("path")
          .datum(series.values)
          .attr("fill", "none")
          .attr("stroke", series.color || "#000")
          .attr("stroke-width", 4)
          .attr("class", `line-${series.field.replace(/\s+/g, "-")}`)
          .attr("d", line);

        svg.append("defs")
          .append("clipPath")
          .attr("id", "clip")
          .append("rect")
          .attr("x", margin.left)
          .attr("y", 0)
          .attr("width", width - margin.left - margin.right)
          .attr("height", height);

        const pointCount = series.values.length;
        const dotRadius = 4;

        chartGroup.selectAll(`.point-${series.field.replace(/\s+/g, "-")}`)
          .data(series.values.
            map((value, index) => ({ value, field: series.field, index }))
            .filter(d => d.value !== 0))
          .enter()
          .append("circle")
          .attr("class", `point-${series.field.replace(/[^a-zA-Z0-9_-]/g, "")}`)
          .attr("cx", d => xScale(d.index))
          .attr("cy", d => {
            const key = `${d.field}_${d.index}`;
            const base = baseYDataRef.current.find(s => s.field === d.field)?.values[d.index] || 0;
            const pivot = pivotState[key] || 0;
            const sw = switchState[key] || 0;
            const l = lineState[key] || 0;
            const value = base + pivot + sw + l;

            return yScale(Math.max(0, value));
          })

          .attr("r", dotRadius)
          .attr("fill", series.color || "#000")
          .on("click", (event, d) => {
            if (!drag) return;
            const [mouseX, mouseY] = d3.pointer(event);
            const menuWidth = 160;
            const menuHeight = 70;
            const padding = 10;

            const clampedX = Math.min(mouseX, chartWidth - menuWidth - padding);
            const clampedY = Math.min(mouseY, chartHeight - menuHeight - padding);

            setLeftClickMenu({
              x: clampedX,
              y: clampedY,
              block_no: xData[d.index],
              index: d.index,
              seriesName: d.field,
            });
          })
          .on("contextmenu", (event) => {
            if (!drag) return;
            event.preventDefault();
            event.stopPropagation();
            const [x, y] = d3.pointer(event);
            setContextMenu({ x, y });
            setLeftClickMenu(null);
          })
          .on("mouseover", (event, d) => {
            const [x, y] = d3.pointer(event, svg.node());
            const index = d.index;

            let tooltipHTML = `
               <div style="color: white; font-size: 14px; font-weight: bold;">
                 ${xData[index]}
               </div>
             `;

            baseYDataRef.current.forEach(series => {
              const field = series.field;
              const key = `${field}_${index}`;
              const base = series.values[index] || 0;
              const pivot = pivotState[key] || 0;
              const sw = switchState[key] || 0;
              const l = lineState[key] || 0;
              const finalY = base + pivot + sw + l;

              tooltipHTML += `
                 <div style="display: flex; align-items: center; margin-top: 2px;">
                   <div style="width: 10px; height: 10px; background-color: ${series.color}; margin-right: 5px; border: 1px solid white;"></div>
                   <span style="font-size: 13px; font-weight: bold;">${field}:</span> 
                   <span style="margin-left: 5px; font-size: 13px;">${finalY.toLocaleString()}</span>
                 </div>
               `;
            });

            const tooltipWidth = 200;
            const tooltipHeight = 100;
            const padding = 20;

            let tooltipX = x + 10;
            let tooltipY = y - 10;

            if (tooltipX + tooltipWidth > chartWidth) {
              tooltipX = chartWidth - tooltipWidth - padding;
            }

            if (tooltipY < 0) {
              tooltipY = padding;
            }

            tooltip
              .style("opacity", 1)
              .style("left", `${tooltipX}px`)
              .style("top", `${tooltipY}px`)
              .html(tooltipHTML);
          })
          .on("mouseout", () => tooltip.style("opacity", 0))
        if (drag) {
          chartGroup.selectAll(`.point-${series.field.replace(/[^a-zA-Z0-9_-]/g, "")}`)
            .call(
              d3.drag()
                .on("start", (event, d) => {
                  const [, startY] = d3.pointer(event, svgRef.current);
                  const key = `${d.field}_${d.index}`;
                  const base = baseYDataRef.current.find(s => s.field === d.field)?.values[d.index] || 0;
                  const pivot = pivotState[key] || 0;
                  const sw = switchState[key] || 0;
                  const l = lineState[key] || 0;
                  const currentValue = base + pivot + sw + l;

                  const yPixel = yScaleRef.current(currentValue);
                  const offsetY = startY - yPixel;

                  dragContextRef.current = {
                    startY,
                    key,
                    field: d.field,
                    index: d.index,
                    base,
                    originalValue: currentValue,
                    offsetY
                  };
                })

                .on("drag", (event) => {
                  const context = dragContextRef.current;
                  if (!context) return;

                  const [, rawY] = d3.pointer(event, svgRef.current);
                  const yScaleSafe = yScaleRef.current || yScale;
                  const currentY = rawY - (context.offsetY || 0);
                  let draggedValue = yScaleSafe.invert(currentY);
                  if (draggedValue < 0) draggedValue = 0;

                  const key = context.key;
                  const field = context.field;
                  const index = context.index;
                  const base = context.base;
                  const delta = draggedValue - base;

                  let maxProjectedValue = draggedValue;

                  if (action === "line") {
                    setLineState(prev => ({ ...prev, [key]: delta }));
                    maxProjectedValue = base + delta;
                  }

                  else if (action === "pivot" && startTime !== null && selectedParameter) {
                    const startIndex = startTime;
                    const currentIndex = index;
                    const baseData = baseYDataRef.current.find(s => s.field === selectedParameter);
                    if (!baseData) return;

                    const fromIndex = Math.min(startIndex, currentIndex);
                    const toIndex = Math.max(startIndex, currentIndex);
                    const endBaseVal = baseData.values[currentIndex];
                    const totalDelta = draggedValue - endBaseVal;
                    const pivotUpdates = { ...pivotState };

                    for (let i = fromIndex + 1; i <= toIndex; i++) {
                      const ratio = (i - fromIndex) / (toIndex - fromIndex);
                      const adj = totalDelta * (startIndex < currentIndex ? ratio : 1 - ratio);
                      const stepKey = `${selectedParameter}_${i}`;
                      const originalY = baseData.values[i] + (pivotState[stepKey] || 0);
                      const projectedY = originalY + adj;

                      maxProjectedValue = Math.max(maxProjectedValue, projectedY);
                      pivotUpdates[stepKey] = (pivotState[stepKey] || 0) + adj;
                    }

                    setPivotState(pivotUpdates);
                  }

                  else if (action === "switch" && startTime !== null && endTime !== null && selectedParameter) {
                    const startIndex = startTime;
                    const endIndex = endTime;
                    const fromIndex = Math.min(startIndex, endIndex);
                    const toIndex = Math.max(startIndex, endIndex);
                    const startBaseVal = baseYDataRef.current.find(s => s.field === selectedParameter)?.values[startIndex] || 0;
                    const keyStart = `${selectedParameter}_${startIndex}`;
                    const currentOffset = (pivotState[keyStart] || 0) + (switchState[keyStart] || 0) + (lineState[keyStart] || 0);
                    const rawOffset = draggedValue - (startBaseVal + currentOffset);

                    const switchUpdates = { ...switchState };

                    for (let i = fromIndex; i <= toIndex; i++) {
                      const baseVal = baseYDataRef.current.find(s => s.field === selectedParameter)?.values[i] || 0;
                      const stepKey = `${selectedParameter}_${i}`;
                      const currentVal = baseVal + (pivotState[stepKey] || 0) + (switchState[stepKey] || 0) + (lineState[stepKey] || 0);
                      const projectedVal = currentVal + rawOffset;

                      maxProjectedValue = Math.max(maxProjectedValue, projectedVal);
                      switchUpdates[stepKey] = (switchState[stepKey] || 0) + rawOffset;
                    }

                    setSwitchState(switchUpdates);
                  }

                  if (draggedValue < yMin || maxProjectedValue > yMax) {
                    if (maxProjectedValue > yMax) {
                      const newMax = Math.ceil(maxProjectedValue / 1000) * 1000 + 1000;
                      setYMax(prev => Math.max(prev, newMax));
                    }

                    if (draggedValue < yMin) {
                      const paddedMin = Math.floor(draggedValue / 1000) * 1000 - 1000;
                      setYMin(prev => Math.min(prev, paddedMin));
                    }
                  }
                })

                .on("end", () => {
                  dragContextRef.current = {};
                  tooltip.transition().duration(200).style("opacity", 0);
                })
            )
        }
      });

      const zoom = d3.zoom()
        .scaleExtent([1, 10])
        .translateExtent([[margin.left, 0], [width - margin.right, height]])
        .extent([[margin.left, 0], [width - margin.right, height]])
        .on("zoom", (event) => {
          zoomTransformRef.current = event.transform;
          const newX = event.transform.rescaleX(xScale);
          xScaleRef.current = newX;

          const newXAxis = d3.axisBottom(newX)
            .tickFormat(i => xData[Math.round(i)] || "")
            .ticks(xData.length / interval);

          xAxisGroup.call(newXAxis)
            .selectAll("text")
            .attr("transform", "rotate(-45)")
            .style("text-anchor", "end")
            .style("font-size", "14px")
            .style("font-weight", "bold");

          yData.forEach(series => {
            if (hiddenSeriesMap[series.field]) return;
            const safeField = series.field.replace(/[^a-zA-Z0-9_-]/g, "");
            const dotRadius = 4;

            const updatedLine = d3.line()
              .defined((_, i) => {
                const base = baseYDataRef.current.find(s => s.field === series.field)?.values[i];
                return base !== null && base !== undefined && base !== 0;
              })
              .x((_, i) => newX(i))
              .y((_, i) => {
                const key = `${series.field}_${i}`;
                const base = baseYDataRef.current.find(s => s.field === series.field)?.values[i] || 0;
                const pivot = pivotState[key] || 0;
                const sw = switchState[key] || 0;
                const l = lineState[key] || 0;
                return yScale(base + pivot + sw + l);
              });

            chartGroup.select(`.line-${safeField}`)
              .attr("d", updatedLine(series.values));

            chartGroup.selectAll(`.point-${safeField}`)
              .data(
                series.values.map((_, i) => {
                  const key = `${series.field}_${i}`;
                  const base = baseYDataRef.current.find(s => s.field === series.field)?.values[i];
                  const pivot = pivotState[key] || 0;
                  const sw = switchState[key] || 0;
                  const l = lineState[key] || 0;

                  return {
                    index: i,
                    value: base + pivot + sw + l,
                    base,
                    field: series.field
                  };
                }).filter(d => d.base !== null && d.base !== undefined && d.base !== 0)
              )
              .join("circle")
              .attr("class", `point-${safeField}`)
              .attr("cx", d => newX(d.index))
              .attr("cy", d => yScale(d.value))
              .attr("r", dotRadius)
              .attr("fill", series.color || "#000");
          });
        });

      svg.call(zoom).call(zoom.transform, zoomTransformRef.current);

    }, [
      xData, yData, chartWidth, chartHeight, yMax, action,
      startTime, endTime, selectedParameter,
      pivotState, switchState, lineState, hiddenSeriesMap
    ]);

    const handleLeftMenuSelect = (type) => {
      if (type === "start") {
        setStartTime(leftClickMenu.index);
      } else if (type === "end") {
        setEndTime(leftClickMenu.index);
      }
      setSelectedParameter(leftClickMenu.seriesName);
      setLeftClickMenu(null);
    };

    const handleActionSelect = (actionType) => {
      setAction(actionType);
      setContextMenu(null);
      if (actionType === "pivot") {
        setEndTime(null);
      } else if (actionType === "line") {
        setLeftClickMenu(null);
      }
    };

    return (
      <div className="chart-container">
<svg ref={svgRef} width="100%" height="800"></svg>
        <div ref={tooltipRef}></div>
        {contextMenu && (
          <div
            className="context-menu"
            style={{
              position: "absolute",
              top: `${contextMenu.y}px`,
              left: `${contextMenu.x}px`,
            }}
          >
            <div onClick={() => handleActionSelect("switch")}>Shift</div>
            <div onClick={() => handleActionSelect("pivot")}>Pivot</div>
            <div onClick={() => handleActionSelect("line")}>Drag</div>
          </div>
        )}

        {leftClickMenu && action !== "line" && (
          <div
            ref={menuRef}
            className="left-click-menu"
            style={{
              position: "absolute",
              top: `${leftClickMenu.y}px`,
              left: `${leftClickMenu.x}px`,
            }}
          >
            <div onClick={() => handleLeftMenuSelect("start")}>Set Start</div>
            <div onClick={() => handleLeftMenuSelect("end")}>Set End</div>
          </div>
        )}

        {/* Toggle Button */}
        <button
          onClick={() => setShowLegend(!showLegend)}
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 10,
            padding: "6px 12px",
            backgroundColor: "transparent",
            color: "black",
            borderRadius: "4px",
            cursor: "pointer"
          }}
        >
          {showLegend ? <MdLabelOff size={24} /> : <MdLabel size={24} />}
        </button>

        {/* Legend Panel */}
        {showLegend && (
          <div
            style={{
              position: "absolute",
              top: 50,
              right: 10,
              width: "220px",
              maxHeight: "60%",
              overflowY: "auto",
              backgroundColor: "#fff",
              boxShadow: "0 0 10px rgba(0,0,0,0.2)",
              borderRadius: "6px",
              padding: "10px",
              zIndex: 9
            }}
          >
            <input
              type="text"
              value={legendSearch}
              onChange={(e) => setLegendSearch(e.target.value)}
              placeholder="Search series..."
              style={{
                width: "100%",
                padding: "6px",
                marginBottom: "10px",
                border: "1px solid #ccc",
                borderRadius: "4px"
              }}
            />
            {yData
              .filter((series) =>
                series.field.toLowerCase().includes(legendSearch.toLowerCase())
              )
              .map((series, i) => {
                const lineClass = `.line-${series.field.replace(/\s+/g, "-")}`;
                const pointClass = `.point-${series.field.replace(/\s+/g, "-")}`;
                const safeClass = `legend-${series.field.replace(/\s+/g, "-")}`;
                const isHidden = hiddenSeriesMap[series.field] || false;

                return (
                  <div
                    key={i}
                    className={safeClass}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      marginBottom: "6px"
                    }}
                    onClick={() => {
                      const line = d3.select(lineClass);
                      const points = d3.selectAll(pointClass);
                      const display = isHidden ? "block" : "none";
                      line.style("display", display);
                      points.style("display", display);
                      setHiddenSeriesMap(prev => ({
                        ...prev,
                        [series.field]: !prev[series.field]
                      }));
                    }}
                  >
                    <div
                      style={{
                        width: "14px",
                        height: "14px",
                        backgroundColor: series.color || "#000",
                        marginRight: "6px",
                        border: "1px solid #000"
                      }}
                    ></div>
                    <span
                      style={{
                        textDecoration: isHidden ? "line-through" : "none",
                        color: "#333",
                        fontSize: "13px"
                      }}
                    >
                      {series.field}
                    </span>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    );
  });

export default Charts;